import React, { useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Copy, Truck, CheckCircle2, Circle, Package, Download } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Paid" },
  { value: "shipped", label: "Shipped" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "cancelled", label: "Cancelled" },
];

const statusColor = (s) => ({
  pending: "#B08968",
  confirmed: "#2E7D32",
  shipped: "#1565C0",
  fulfilled: "#4A4A4A",
  cancelled: "#B23A48",
}[s] || "#666");

const copyText = (t) => {
  navigator.clipboard?.writeText(t);
  toast.success("Copied");
};

const formatAddress = (a) => {
  if (!a) return "";
  return [
    a.name,
    a.line1 + (a.line2 ? `, ${a.line2}` : ""),
    `${a.city}, ${a.province} ${a.postal_code}`,
    a.country,
    a.phone,
  ].filter(Boolean).join("\n");
};

export default function OrdersAdmin({ orders, reload }) {
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "unshipped") return orders.filter(o => o.payment_status === "paid" && !["shipped", "fulfilled", "cancelled"].includes(o.status));
    if (filter === "paid") return orders.filter(o => o.payment_status === "paid");
    return orders.filter(o => o.status === filter);
  }, [orders, filter]);

  const counts = useMemo(() => ({
    all: orders.length,
    paid: orders.filter(o => o.payment_status === "paid").length,
    unshipped: orders.filter(o => o.payment_status === "paid" && !["shipped", "fulfilled", "cancelled"].includes(o.status)).length,
    shipped: orders.filter(o => o.status === "shipped").length,
    fulfilled: orders.filter(o => o.status === "fulfilled").length,
    pending: orders.filter(o => o.status === "pending").length,
  }), [orders]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/orders/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `kaiya-orders-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="orders-admin">
      {/* Filter chips + Export */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[
          { id: "all", label: `All (${counts.all})` },
          { id: "unshipped", label: `To ship (${counts.unshipped})` },
          { id: "paid", label: `Paid (${counts.paid})` },
          { id: "pending", label: `Pending (${counts.pending})` },
          { id: "shipped", label: `Shipped (${counts.shipped})` },
          { id: "fulfilled", label: `Fulfilled (${counts.fulfilled})` },
        ].map(f => (
          <button
            key={f.id}
            data-testid={`orders-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className="px-4 py-1.5 rounded-full text-xs border"
            style={{
              borderColor: "var(--kaiya-text)",
              background: filter === f.id ? "var(--kaiya-text)" : "transparent",
              color: filter === f.id ? "var(--kaiya-bg)" : "var(--kaiya-text)",
              opacity: filter === f.id ? 1 : 0.7,
            }}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto">
          <button
            data-testid="orders-export-csv"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs border"
            style={{ borderColor: "var(--kaiya-primary)", color: "var(--kaiya-primary)" }}
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="opacity-60 text-center py-12 kaiya-card">
          <Package size={32} className="mx-auto mb-3 opacity-50" />
          No orders in this view.
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(o => (
          <OrderRow
            key={o.id}
            order={o}
            open={openId === o.id}
            onToggle={() => setOpenId(openId === o.id ? null : o.id)}
            onChanged={reload}
          />
        ))}
      </div>
    </div>
  );
}

function OrderRow({ order, open, onToggle, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [tracking, setTracking] = useState(order.shipment?.tracking_code || "");
  const [carrier, setCarrier] = useState(order.shipment?.carrier || "Chit Chats");
  const [notes, setNotes] = useState(order.admin_notes || "");

  const update = async (patch) => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}`, patch);
      toast.success("Order updated");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const setStatus = (status) => update({ status });
  const saveShipping = () => update({ tracking_code: tracking, carrier });
  const saveNotes = () => update({ admin_notes: notes });

  const a = order.address || {};
  const addrText = formatAddress(a);
  const isPaid = order.payment_status === "paid";

  return (
    <div
      className="kaiya-card overflow-hidden"
      data-testid={`admin-order-${order.id}`}
      style={{ borderLeft: `4px solid ${statusColor(order.status)}` }}
    >
      {/* Summary row */}
      <button
        onClick={onToggle}
        data-testid={`order-toggle-${order.id}`}
        className="w-full p-5 flex items-center justify-between text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-mono text-xs opacity-60">#{order.id.slice(0, 8)}</div>
            <span
              className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ background: statusColor(order.status), color: "white" }}
            >
              {order.status}
            </span>
            <span
              className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full border"
              style={{ borderColor: isPaid ? "#2E7D32" : "#B08968", color: isPaid ? "#2E7D32" : "#B08968" }}
            >
              {order.payment_status}
            </span>
          </div>
          <div className="font-medium mt-2 truncate">{a.name || "—"} · {order.email}</div>
          <div className="text-xs opacity-60 mt-0.5">
            {new Date(order.created_at).toLocaleString()} · {order.line_items?.length || 0} item{(order.line_items?.length || 0) === 1 ? "" : "s"} · {order.shipping_label}
          </div>
        </div>
        <div className="flex items-center gap-4 pl-4">
          <div className="text-right">
            <div className="font-serif-display text-xl">${order.total.toFixed(2)}</div>
            <div className="text-[10px] opacity-60 uppercase tracking-wider">{order.currency || "CAD"}</div>
          </div>
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {open && (
        <div className="border-t px-5 py-6 space-y-6" style={{ borderColor: "var(--kaiya-border)", background: "var(--kaiya-bg-2)" }}>
          {/* Grid: Ship to + Items */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Shipping address */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow">Ship to</div>
                <button
                  data-testid={`order-copy-address-${order.id}`}
                  onClick={() => copyText(addrText)}
                  className="text-xs underline inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                >
                  <Copy size={12} /> Copy full address
                </button>
              </div>
              <div className="kaiya-card p-4 text-sm leading-relaxed whitespace-pre-line" style={{ background: "var(--kaiya-bg)" }}>
                {addrText || "No address on file"}
              </div>
              {a.email && (
                <div className="mt-3 text-xs opacity-70">
                  Email: <a className="underline" href={`mailto:${a.email}`}>{a.email}</a>
                  {a.phone && <> · Phone: <a className="underline" href={`tel:${a.phone}`}>{a.phone}</a></>}
                </div>
              )}
            </div>

            {/* Line items */}
            <div>
              <div className="label-eyebrow mb-3">Items</div>
              <div className="kaiya-card p-4 space-y-3" style={{ background: "var(--kaiya-bg)" }}>
                {(order.line_items || []).map((li, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    {li.image && <img src={li.image} alt={li.name} className="w-12 h-14 object-cover rounded-md" />}
                    <div className="flex-1 text-sm">
                      <div className="font-medium">{li.name}</div>
                      {li.variant && <div className="text-xs opacity-60">{li.variant}</div>}
                      <div className="text-xs opacity-70 mt-0.5">Qty {li.quantity} · ${li.unit_price.toFixed(2)}</div>
                    </div>
                    <div className="font-serif-display text-base">${li.line_total.toFixed(2)}</div>
                  </div>
                ))}
                <div className="border-t pt-3 mt-3 space-y-1 text-sm" style={{ borderColor: "var(--kaiya-border)" }}>
                  <div className="flex justify-between opacity-70"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between opacity-70"><span>Shipping</span><span>${order.shipping.toFixed(2)}</span></div>
                  <div className="flex justify-between font-medium pt-1"><span>Total</span><span>${order.total.toFixed(2)}</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Fulfillment */}
          <div>
            <div className="label-eyebrow mb-3">Fulfillment</div>
            <div className="grid md:grid-cols-3 gap-3 items-start">
              <div>
                <label className="text-xs opacity-70 mb-1 block">Carrier</label>
                <input
                  data-testid={`order-carrier-${order.id}`}
                  className="kaiya-input w-full"
                  value={carrier}
                  onChange={e => setCarrier(e.target.value)}
                  placeholder="Chit Chats / Canada Post"
                />
              </div>
              <div>
                <label className="text-xs opacity-70 mb-1 block">Tracking code</label>
                <input
                  data-testid={`order-tracking-${order.id}`}
                  className="kaiya-input w-full"
                  value={tracking}
                  onChange={e => setTracking(e.target.value)}
                  placeholder="e.g. CC123456789"
                />
              </div>
              <div className="flex items-end">
                <button
                  data-testid={`order-save-tracking-${order.id}`}
                  onClick={saveShipping}
                  disabled={saving}
                  className="btn-secondary w-full disabled:opacity-50"
                >
                  Save tracking
                </button>
              </div>
            </div>
          </div>

          {/* Status actions */}
          <div>
            <div className="label-eyebrow mb-3">Update status</div>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(s => (
                <button
                  key={s.value}
                  data-testid={`order-status-${order.id}-${s.value}`}
                  disabled={saving || order.status === s.value}
                  onClick={() => setStatus(s.value)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs border transition disabled:opacity-40"
                  style={{
                    borderColor: statusColor(s.value),
                    background: order.status === s.value ? statusColor(s.value) : "transparent",
                    color: order.status === s.value ? "white" : statusColor(s.value),
                  }}
                >
                  {order.status === s.value ? <CheckCircle2 size={12} /> : <Circle size={12} />}
                  {s.label}
                </button>
              ))}
              {isPaid && order.status !== "shipped" && (
                <button
                  data-testid={`order-mark-shipped-${order.id}`}
                  onClick={() => setStatus("shipped")}
                  disabled={saving}
                  className="btn-primary inline-flex items-center gap-2 text-xs py-2 px-4"
                >
                  <Truck size={12} /> Mark shipped
                </button>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <div className="label-eyebrow mb-3">Internal notes</div>
            <textarea
              data-testid={`order-notes-${order.id}`}
              rows={2}
              className="kaiya-input w-full"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Anything the team should know (gift note, delayed, etc.)"
            />
            <button
              data-testid={`order-save-notes-${order.id}`}
              onClick={saveNotes}
              disabled={saving}
              className="btn-secondary mt-2 text-xs py-2 px-4 disabled:opacity-50"
            >
              Save notes
            </button>
          </div>

          {/* Meta */}
          <div className="text-xs opacity-60 grid md:grid-cols-2 gap-1 pt-3 border-t" style={{ borderColor: "var(--kaiya-border)" }}>
            <div>Order ID: <span className="font-mono">{order.id}</span></div>
            <div>Stripe session: <span className="font-mono">{order.stripe_session_id}</span></div>
            {order.paid_at && <div>Paid at: {new Date(order.paid_at).toLocaleString()}</div>}
            {order.shipped_at && <div>Shipped at: {new Date(order.shipped_at).toLocaleString()}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
